import { createContext, useContext } from 'react';

import type { TermsGateStatus } from './terms-gate-status';

/**
 * The Terms-of-Service acceptance context (ADR-0497), split from
 * `TermsAcceptanceProvider.tsx` for the same reason `i18n-context.ts` is split
 * from `I18nProvider.tsx`: a module that exports both a component and a hook
 * defeats React Fast Refresh, so the provider file exports only its component.
 */

export interface TermsAcceptanceContextValue {
  readonly status: TermsGateStatus;
  /** Records acceptance of the version currently in force. */
  readonly accept: () => Promise<void>;
  readonly accepting: boolean;
  /** True when the last acceptance attempt failed. Cleared by the next attempt. */
  readonly acceptFailed: boolean;
}

export const TermsAcceptanceContext = createContext<TermsAcceptanceContextValue | null>(null);

/**
 * Throws outside the provider rather than returning a default — deliberate, and
 * the same choice `useT()` makes. A silent default here would mean a component
 * rendering as though the terms were accepted when nothing had actually asked.
 */
export function useTermsAcceptance(): TermsAcceptanceContextValue {
  const value = useContext(TermsAcceptanceContext);
  if (value === null) {
    throw new Error('useTermsAcceptance must be used within a TermsAcceptanceProvider');
  }
  return value;
}
